import { CommentMention } from '../../types/comments';
import { User } from '../../types/mold';

export interface MentionQuery {
  start: number;
  end: number;
  query: string;
}

export interface MentionSegment {
  text: string;
  mention?: CommentMention;
}

const mentionTokenPattern = /(^|\s)@([A-Za-z0-9._-]*)$/;
const savedMentionPattern = /@([A-Za-z0-9._-]+)/g;
const normalizeSearchText = (value: string) =>
  value.toLowerCase().replace(/[\s._-]+/g, '');

const getFuzzyScore = (value: string, query: string): number | null => {
  const normalizedValue = normalizeSearchText(value);
  const normalizedQuery = normalizeSearchText(query);

  if (!normalizedQuery) {
    return 0;
  }
  if (normalizedValue.startsWith(normalizedQuery)) {
    return 1;
  }
  if (normalizedValue.includes(normalizedQuery)) {
    return 2;
  }

  let queryIndex = 0;
  for (const char of normalizedValue) {
    if (char === normalizedQuery[queryIndex]) {
      queryIndex += 1;
      if (queryIndex === normalizedQuery.length) {
        return 3;
      }
    }
  }

  return null;
};

export function findMentionQuery(value: string, caretIndex: number): MentionQuery | null {
  const beforeCaret = value.slice(0, caretIndex);
  const match = beforeCaret.match(mentionTokenPattern);
  if (!match) {
    return null;
  }

  const token = `@${match[2]}`;
  return {
    start: caretIndex - token.length,
    end: caretIndex,
    query: match[2],
  };
}

export function insertMention(
  value: string,
  mentionQuery: MentionQuery,
  username: string,
): { value: string; caretIndex: number } {
  const replacement = `@${username} `;
  const nextValue = `${value.slice(0, mentionQuery.start)}${replacement}${value.slice(mentionQuery.end)}`;
  return {
    value: nextValue,
    caretIndex: mentionQuery.start + replacement.length,
  };
}

export function getMentionSuggestions(
  users: User[],
  query: string,
  limit: number = 6,
): User[] {
  const normalizedQuery = query.trim().toLowerCase();

  return users
    .filter((user) => user.isActive !== false)
    .map((user) => {
      if (!normalizedQuery) {
        return { user, score: 0 };
      }

      const scores = [
        getFuzzyScore(user.fullname, normalizedQuery),
        getFuzzyScore(user.username, normalizedQuery),
      ].filter((score): score is number => score !== null);

      if (scores.length === 0) {
        return null;
      }

      return { user, score: Math.min(...scores) };
    })
    .filter((result): result is { user: User; score: number } => result !== null)
    .sort((a, b) => a.score - b.score || a.user.fullname.localeCompare(b.user.fullname))
    .map((result) => result.user)
    .slice(0, limit);
}

export function extractMentions(value: string, users: User[]): CommentMention[] {
  const usersByUsername = new Map(
    users.map((user) => [user.username.toLowerCase(), user]),
  );

  const mentions: CommentMention[] = [];
  for (const match of value.matchAll(savedMentionPattern)) {
    const username = match[1];
    const user = usersByUsername.get(username.toLowerCase());
    if (!user || match.index == null) {
      continue;
    }

    mentions.push({
      userId: user.id,
      username: user.username,
      fullname: user.fullname,
      startIndex: match.index,
      endIndex: match.index + match[0].length,
    });
  }

  return mentions;
}

export function buildMentionSegments(
  body: string,
  mentions: CommentMention[],
): MentionSegment[] {
  const validMentions = mentions
    .filter((mention) =>
      Number.isInteger(mention.startIndex) &&
      Number.isInteger(mention.endIndex) &&
      mention.startIndex >= 0 &&
      mention.endIndex > mention.startIndex &&
      mention.endIndex <= body.length,
    )
    .sort((a, b) => a.startIndex - b.startIndex);

  const segments: MentionSegment[] = [];
  let cursor = 0;

  for (const mention of validMentions) {
    if (mention.startIndex < cursor) {
      continue;
    }

    if (mention.startIndex > cursor) {
      segments.push({ text: body.slice(cursor, mention.startIndex) });
    }

    segments.push({
      text: body.slice(mention.startIndex, mention.endIndex),
      mention,
    });
    cursor = mention.endIndex;
  }

  if (cursor < body.length) {
    segments.push({ text: body.slice(cursor) });
  }

  return segments.length > 0 ? segments : [{ text: body }];
}
